import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api-client';
import type { Project, ProjectWithRole } from '../types/api';

export const projectsKeys = {
  all: ['projects'] as const,
  list: () => [...projectsKeys.all, 'list'] as const,
  byId: (id: string) => [...projectsKeys.all, 'byId', id] as const,
};

interface ProjectsListResponse {
  projects: ProjectWithRole[];
}

export function useProjectsList() {
  return useQuery({
    queryKey: projectsKeys.list(),
    queryFn: () => api.get<ProjectsListResponse>('/projects'),
    select: (d) => d.projects,
  });
}

export function useProject(id: string | null) {
  return useQuery({
    queryKey: id ? projectsKeys.byId(id) : ['projects', 'none'],
    enabled: !!id,
    queryFn: async () => {
      const res = await api.get<{ project: ProjectWithRole }>(`/projects/${id}`);
      return res.project;
    },
  });
}

export function useCreateProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      name: string;
      /** Phase 12: every new project must be tied to a company. */
      company_id: string;
      /** apps.key this project is namespaced under, e.g. `running_title`. */
      app_key: string;
      description?: string | null;
      owner_id?: string;
    }) => api.post<{ project: Project }>('/projects', input),
    onSuccess: () => qc.invalidateQueries({ queryKey: projectsKeys.all }),
  });
}
